import { h, render, Fragment } from "preact";
import { useState, useEffect } from "preact/hooks";
import storefront from "../helpers/storefront";
import { getCompareModalData } from "../helpers/storefront/queries";
import unwrapNodes from "../helpers/unwrapNodes";
import CompareModalRow from "./compareModalRow";
import RichText from "../helpers/richText";
import DimensionRowContent from "./dimensionRowContent";
import ModelRowContentItem from "./modelRowContentItem";
import CompareModalCta from "./compareModalCta";
import CompareBuyCta from "./compareBuyCta";
import ContentRowContent from "./contentRowContent";
import CompatibleProduct from "./compatibleProduct";
import CompatibilityRowContent from "./compatibilityRowContent";
import ModelSelector from "./modelSelector";

export default function CompareModalContent({ handle }) {
  const [data, setData] = useState();
  const [compareProducts, setCompareProducts] = useState([]);
  const [contentRowCount, setContentRowCount] = useState([]);

  const setCompareProduct = (id, index) => {
    const newCompareProduct = data.compareProducts.find(
      ({ product }) => product.id === id,
    );

    const newCompareProducts = [...compareProducts];
    newCompareProducts[index] = newCompareProduct;

    setCompareProducts(newCompareProducts);
  };

  useEffect(() => {
    storefront
      .request({
        query: getCompareModalData,
        variables: {
          handle: {
            handle,
            type: "compare_modal",
          },
        },
      })
      .then(({ data }) => {
        const unwrappedData = unwrapNodes(data);

        unwrappedData.compareProducts.forEach((product) => {
          product.dimensionRows = [
            product.dimensionsRow1,
            product.dimensionsRow2,
            product.dimensionsRow3,
            product.dimensionsRow4,
            product.dimensionsRow5,
            product.dimensionsRow6,
          ];
        });

        unwrappedData.dimensionRowHeadings = [
          unwrappedData.dimensionsRow1Heading,
          unwrappedData.dimensionsRow2Heading,
          unwrappedData.dimensionsRow3Heading,
          unwrappedData.dimensionsRow4Heading,
          unwrappedData.dimensionsRow5Heading,
          unwrappedData.dimensionsRow6Heading,
        ].filter((heading) => heading !== null);

        setData(unwrappedData);
        setCompareProducts(unwrappedData.compareProducts);
      });
  }, []);

  useEffect(() => {
    const maxContentRowCount = Math.max(
      0,
      ...compareProducts.map(
        (compareProduct) => compareProduct.compareContent?.length || 0,
      ),
    );
    setContentRowCount(maxContentRowCount);
  }, [compareProducts]);

  if (!data) {
    return <div className="compare-modal-v2__loading">Loading...</div>;
  }

  return (
    <div class="compare-modal-v2">
      <div className="compare-modal-v2__header">
        <div>
          <h2 className={`compare-modal-v2__heading h2`}>{data.heading}</h2>
          {data.copy && (
            <RichText schema={data.copy} className="compare-modal-v2__copy" />
          )}
        </div>
        {<CompareModalCta text={data.ctaText} url={data.ctaUrl} />}
      </div>
      <div className="compare-modal-v2__model-selector-row">
        <h3 className="compare-modal-v2__row-title compare-modal-v2__model-title">
          Model
        </h3>
        <div className="compare-modal-v2__row-items compare-modal-v2__model-selector-row-items">
          {compareProducts.map((product, index) => (
            <ModelSelector
              compareProduct={product}
              products={data.compareProducts}
              index={index}
              setCompareProduct={setCompareProduct}
            />
          ))}
        </div>
      </div>
      <div className="compare-modal-v2__row-items">
        {compareProducts.map((product) => (
          <ModelRowContentItem compareProduct={product} />
        ))}
      </div>
      <div className="compare-modal-v2__row-items compare-modal-v2__model-buy-row">
        {compareProducts.map((product) => (
          <CompareBuyCta compareProduct={product} />
        ))}
      </div>
      <div className="compare-modal-v2__overview-row-heading">Overview</div>
      <div className="compare-modal-v2__row-items compare-modal-v2__overview-row">
        {compareProducts.map((product) => (
          <div className="compare-modal-v2__row-item">
            {
              <RichText
                className="compare-modal-v2__overview-description"
                schema={product.description}
              />
            }
          </div>
        ))}
      </div>
      <CompareModalRow className="compare-modal-v2__content-row">
        {[...Array(contentRowCount)].map((_, i) => (
          <ContentRowContent products={compareProducts} rowIndex={i} />
        ))}
      </CompareModalRow>
      <CompareModalRow title="Dimensions" accordion={true}>
        {data.dimensionRowHeadings.map((heading, i) => (
          <>
            <div className="compare-modal-v2__dimension-row-heading">
              {heading}
            </div>
            <DimensionRowContent products={compareProducts} rowIndex={i} />
          </>
        ))}
      </CompareModalRow>
      <CompareModalRow title="Key Features" accordion={true}>
        <div className="compare-modal-v2__row-items compare-modal-v2__key-features-row-items">
          {compareProducts.map((product) => (
            <div className="compare-modal-v2__row-item">
              {
                <RichText
                  className="compare-modal-v2__rte"
                  schema={product.keyFeatures}
                />
              }
            </div>
          ))}
        </div>
      </CompareModalRow>
      {data.compatibleProductList && data.compatibleProductList.length > 0 && (
        <div className="compare-modal-v2__row compare-modal-v2__compatible-products">
          <h3 className="compare-modal-v2__row-title compare-modal-v2__compatible-products-title">
            Compatibility
          </h3>
          <div className="compare-modal-v2__compatible-products-content">
            <div className="compare-modal-v2__compatible-products-heading">
              {data.compatibleProductListHeading}
            </div>
            <div className="compare-modal-v2__compatible-products-items">
              {data.compatibleProductList.map((compatibleProduct) => (
                <CompatibleProduct product={compatibleProduct} />
              ))}
            </div>
          </div>
        </div>
      )}
      {data.compatibilityGrid && data.compatibilityGrid.length > 0 && (
        <CompareModalRow title="Compatibility">
          {data.compatibilityGrid.map((product) => (
            <>
              <div className="compare-modal-v2__dimension-row-heading">
                {product.title}
              </div>
              <CompatibilityRowContent
                products={compareProducts}
                rowProduct={product}
              />
            </>
          ))}
        </CompareModalRow>
      )}
      <CompareModalRow className="compare-modal-v2__buy-row">
        <div className="compare-modal-v2__row-items">
          {compareProducts.map((product) => (
            <CompareBuyCta compareProduct={product} />
          ))}
        </div>
      </CompareModalRow>
    </div>
  );
}
